import * as THREE from 'three';
import type { FacilityRoom, RoomSide } from './FacilityDressing';

interface Portal {
  from: string;
  to: string;
  box: THREE.Box3;
}

interface StaticNode {
  obj: THREE.Object3D;
  rooms: string[];
}

const OPP: Record<RoomSide, RoomSide> = { n: 's', s: 'n', e: 'w', w: 'e' };
const HOLD_MS = 180;
const NEAREST_SNAP = 3.2;
const _frustum = new THREE.Frustum();
const _proj = new THREE.Matrix4();
const _box = new THREE.Box3();
const _size = new THREE.Vector3();
const _wp = new THREE.Vector3();
const _camFwd = new THREE.Vector3();
const _toPortal = new THREE.Vector3();

/**
 * Indoor PVS: keep the current room, its door-neighbors, and rooms whose
 * doorway sits in the camera frustum. Everything else is hidden.
 */
export class RoomCuller {
  private readonly rooms: FacilityRoom[];
  private readonly portals: Portal[] = [];
  private readonly staticNodes: StaticNode[] = [];
  private readonly holdUntil = new Map<string, number>();
  private readonly roomBox = new Map<string, THREE.Box3>();
  private lastKey = '';
  private lastHere = '';
  private readonly visible = new Set<string>();
  private readonly mirrorHidden: THREE.Object3D[] = [];

  constructor(root: THREE.Object3D, rooms: FacilityRoom[]) {
    this.rooms = rooms;
    for (const r of rooms) {
      const b = containBounds(r);
      this.roomBox.set(
        r.id,
        new THREE.Box3(
          new THREE.Vector3(b.x0 - 0.55, r.floorY - 0.5, b.z0 - 0.55),
          new THREE.Vector3(b.x1 + 0.55, r.floorY + r.height + 0.5, b.z1 + 0.55),
        ),
      );
    }
    this.portals = buildPortals(rooms);
    root.updateMatrixWorld(true);
    const tracked = new Set<THREE.Object3D>();
    root.traverse((o) => {
      if (skipObject(o)) return;
      const roomsHere = roomsForObject(o, rooms);
      if (roomsHere.length === 0) return;
      this.staticNodes.push({ obj: o, rooms: roomsHere });
      tracked.add(o);
    });
    root.traverse((o) => {
      if (skipObject(o) || tracked.has(o)) return;
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh && !(o as THREE.InstancedMesh).isInstancedMesh) return;
      _box.setFromObject(o);
      if (_box.isEmpty()) return;
      _box.getCenter(_wp);
      const nearest = roomAt(rooms, _wp);
      if (nearest) {
        this.staticNodes.push({ obj: o, rooms: [nearest.id] });
        return;
      }
      o.visible = false;
      o.userData.cullHidden = true;
    });
  }

  update(
    camera: THREE.Camera,
    eye: THREE.Vector3,
    dynamics: THREE.Object3D[],
    opts?: { seeVentFromBath?: boolean },
  ): void {
    _proj.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    _frustum.setFromProjectionMatrix(_proj);
    camera.getWorldDirection(_camFwd);

    let here = roomAt(this.rooms, eye)?.id ?? '';
    if (!here) here = this.lastHere;
    if (here) this.lastHere = here;

    this.visible.clear();
    if (here) {
      this.visible.add(here);
      const extra = this.rooms.find((r) => r.id === here)?.see;
      if (extra) {
        for (const id of extra) {
          if (here === 'bath' && id === 'vent' && !opts?.seeVentFromBath) continue;
          this.visible.add(id);
        }
      }
      // One hop only. World-space frustum hits distant doorways through walls
      // and a BFS then loads the rest of the map.
      for (const p of this.portals) {
        if (p.from !== here) continue;
        if (!portalOpenFrom(p, eye)) continue;
        this.visible.add(p.to);
      }
    }

    const now = performance.now();
    for (const id of this.visible) this.holdUntil.set(id, now + HOLD_MS);
    for (const [id, until] of this.holdUntil) {
      if (until < now) this.holdUntil.delete(id);
      else this.visible.add(id);
    }

    // Never blank the map — if occupancy failed, keep everything up.
    const failSafe = this.visible.size === 0;
    const key = failSafe ? '*' : [...this.visible].sort().join(',');
    if (key !== this.lastKey) {
      this.lastKey = key;
      for (const n of this.staticNodes) {
        applyCull(n.obj, failSafe || n.rooms.some((r) => this.visible.has(r)));
      }
    }

    for (const obj of dynamics) {
      if (!obj) continue;
      obj.getWorldPosition(_wp);
      const r = roomAt(this.rooms, _wp);
      applyCull(obj, failSafe || !r || this.visible.has(r.id));
    }
  }

  /** Hide meshes that live only in these rooms (mirror RT should not pay for the vent loft). */
  hideExclusiveRooms(ids: readonly string[]): void {
    this.restoreExclusiveRooms();
    const ban = new Set(ids);
    for (const n of this.staticNodes) {
      if (n.rooms.length === 0 || !n.rooms.every((r) => ban.has(r))) continue;
      if (!n.obj.visible) continue;
      n.obj.visible = false;
      this.mirrorHidden.push(n.obj);
    }
  }

  restoreExclusiveRooms(): void {
    for (const o of this.mirrorHidden) o.visible = true;
    this.mirrorHidden.length = 0;
  }

  visibleRoomIds(): ReadonlySet<string> {
    return this.visible;
  }
}

function applyCull(obj: THREE.Object3D, show: boolean): void {
  if (obj.userData.dropped || obj.userData.destroyed) return;
  if (show) {
    if (obj.userData.cullHidden) {
      obj.visible = true;
      obj.userData.cullHidden = false;
    }
    return;
  }
  if (obj.visible) {
    obj.visible = false;
    obj.userData.cullHidden = true;
  }
}

function skipObject(o: THREE.Object3D): boolean {
  if ((o as THREE.Light).isLight) return true;
  if (o.name === 'playerAvatar') return true;
  if (o.userData.noRoomCull) return true;
  const mesh = o as THREE.Mesh;
  if (!mesh.isMesh && !(o as THREE.InstancedMesh).isInstancedMesh && o.type !== 'Group') return true;
  if (o.type === 'Group') return !o.name.startsWith('door_');
  return false;
}

function roomsForObject(o: THREE.Object3D, rooms: FacilityRoom[]): string[] {
  const tagged = o.userData.rooms;
  if (Array.isArray(tagged) && tagged.length > 0) return tagged as string[];
  o.getWorldPosition(_wp);
  if ((o as THREE.Mesh).isMesh) {
    _box.setFromObject(o);
    if (!_box.isEmpty()) {
      _box.getCenter(_wp);
      _box.getSize(_size);
      const pad = Math.min(1.2, Math.max(_size.x, _size.z) * 0.15);
      return roomsOverlapping(rooms, _wp, pad);
    }
  }
  return roomsOverlapping(rooms, _wp, 0.55);
}

function containBounds(r: FacilityRoom): { x0: number; x1: number; z0: number; z1: number } {
  return {
    x0: r.walk?.x0 ?? r.x0,
    x1: r.walk?.x1 ?? r.x1,
    z0: r.walk?.z0 ?? r.z0,
    z1: r.walk?.z1 ?? r.z1,
  };
}

function roomsOverlapping(rooms: FacilityRoom[], p: THREE.Vector3, pad: number): string[] {
  const ids: string[] = [];
  for (const r of rooms) {
    const b = containBounds(r);
    if (
      p.x >= b.x0 - pad &&
      p.x <= b.x1 + pad &&
      p.z >= b.z0 - pad &&
      p.z <= b.z1 + pad &&
      p.y >= r.floorY - 0.35 &&
      p.y <= r.floorY + r.height + 0.35
    ) {
      ids.push(r.id);
    }
  }
  return ids;
}

function distToRoom(r: FacilityRoom, p: THREE.Vector3): number {
  const b = containBounds(r);
  const y0 = r.floorY;
  const y1 = r.floorY + r.height;
  const dx = p.x < b.x0 ? b.x0 - p.x : p.x > b.x1 ? p.x - b.x1 : 0;
  const dy = p.y < y0 ? y0 - p.y : p.y > y1 ? p.y - y1 : 0;
  const dz = p.z < b.z0 ? b.z0 - p.z : p.z > b.z1 ? p.z - b.z1 : 0;
  return Math.hypot(dx, dy, dz);
}

/** Highest floor under a point. Ceiling fixtures sit nearer a loft above, so `roomAt` is wrong for debris. */
export function floorBelow(rooms: FacilityRoom[], p: THREE.Vector3): number {
  let best = -Infinity;
  for (const r of rooms) {
    const b = containBounds(r);
    if (p.x < b.x0 - 0.4 || p.x > b.x1 + 0.4) continue;
    if (p.z < b.z0 - 0.4 || p.z > b.z1 + 0.4) continue;
    if (r.floorY <= p.y + 0.25 && r.floorY > best) best = r.floorY;
  }
  if (Number.isFinite(best)) return best;
  return roomAt(rooms, p)?.floorY ?? 0;
}

export function roomAt(rooms: FacilityRoom[], p: THREE.Vector3): FacilityRoom | null {
  let ids = roomsOverlapping(rooms, p, 0.25);
  if (ids.length === 0) ids = roomsOverlapping(rooms, p, 1.4);
  let best: FacilityRoom | null = null;
  let bestFloor = Infinity;
  let bestA = Infinity;
  for (const id of ids) {
    const r = rooms.find((x) => x.id === id);
    if (!r) continue;
    // Stacked rooms share XZ (lab over gen/lockers). Prefer the floor you're on.
    const floorDist = Math.abs(p.y - r.floorY);
    const b = containBounds(r);
    const a = Math.abs(b.x1 - b.x0) * Math.abs(b.z1 - b.z0);
    if (floorDist < bestFloor - 0.05 || (Math.abs(floorDist - bestFloor) <= 0.05 && a < bestA)) {
      bestFloor = floorDist;
      bestA = a;
      best = r;
    }
  }
  if (best) return best;
  let bestD = NEAREST_SNAP;
  for (const r of rooms) {
    const d = distToRoom(r, p);
    if (d < bestD) {
      bestD = d;
      best = r;
    }
  }
  return best;
}

/** Doorway boxes between rooms — used to test closed doors on a hear path. */
export function listRoomPortals(rooms: FacilityRoom[]): Array<{ from: string; to: string; box: THREE.Box3 }> {
  return buildPortals(rooms);
}

/** Door/opening neighbors — used for gunshot carry, not just culling. */
export function adjacentRooms(rooms: FacilityRoom[]): Map<string, Set<string>> {
  const adj = new Map<string, Set<string>>();
  const link = (a: string, b: string) => {
    let s = adj.get(a);
    if (!s) {
      s = new Set();
      adj.set(a, s);
    }
    s.add(b);
  };
  for (const p of buildPortals(rooms)) link(p.from, p.to);
  return adj;
}

/** Doorway walk points for the cheap enemy waypoint graph. Y is the higher floor. */
export function listPortalWaypoints(rooms: FacilityRoom[]): Array<{ x: number; y: number; z: number }> {
  const out: Array<{ x: number; y: number; z: number }> = [];
  const seen = new Set<string>();
  for (const p of buildPortals(rooms)) {
    const a = rooms.find((r) => r.id === p.from);
    const b = rooms.find((r) => r.id === p.to);
    if (!a || !b) continue;
    p.box.getCenter(_wp);
    const y = Math.max(a.floorY, b.floorY);
    const key = `${_wp.x.toFixed(1)},${y.toFixed(1)},${_wp.z.toFixed(1)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ x: _wp.x, y, z: _wp.z });
  }
  return out;
}

export function roomHopCount(adj: Map<string, Set<string>>, fromId: string, toId: string): number {
  if (fromId === toId) return 0;
  const seen = new Set([fromId]);
  let frontier = [fromId];
  let hops = 0;
  while (frontier.length) {
    hops += 1;
    const next: string[] = [];
    for (const id of frontier) {
      for (const n of adj.get(id) ?? []) {
        if (n === toId) return hops;
        if (seen.has(n)) continue;
        seen.add(n);
        next.push(n);
      }
    }
    frontier = next;
  }
  return Infinity;
}

function portalOpenFrom(p: Portal, eye: THREE.Vector3): boolean {
  if (p.box.distanceToPoint(eye) < 2.2) return true;
  if (!_frustum.intersectsBox(p.box)) return false;
  p.box.getCenter(_toPortal);
  _toPortal.sub(eye);
  if (_toPortal.lengthSq() < 1e-4) return true;
  _toPortal.normalize();
  return _camFwd.dot(_toPortal) > 0.12;
}

function buildPortals(rooms: FacilityRoom[]): Portal[] {
  const out: Portal[] = [];
  for (const a of rooms) {
    for (const ga of a.gaps) {
      for (const b of rooms) {
        if (a.id === b.id) continue;
        for (const gb of b.gaps) {
          if (gb.side !== OPP[ga.side]) continue;
          if (Math.abs(ga.along - gb.along) > 2.8) continue;
          if (!wallsNear(a, ga.side, b, 5.2)) continue;
          out.push({ from: a.id, to: b.id, box: portalBox(a, ga.side, ga.along, ga.width ?? 1.15) });
        }
      }
    }
  }
  return out;
}

function wallsNear(a: FacilityRoom, side: RoomSide, b: FacilityRoom, slop: number): boolean {
  if (side === 'n') return Math.abs(a.z1 - b.z0) < slop || Math.abs(a.z1 - b.z1) < slop;
  if (side === 's') return Math.abs(a.z0 - b.z1) < slop || Math.abs(a.z0 - b.z0) < slop;
  if (side === 'e') return Math.abs(a.x1 - b.x0) < slop || Math.abs(a.x1 - b.x1) < slop;
  return Math.abs(a.x0 - b.x1) < slop || Math.abs(a.x0 - b.x0) < slop;
}

function portalBox(room: FacilityRoom, side: RoomSide, along: number, width: number): THREE.Box3 {
  const y0 = room.floorY - 0.15;
  const y1 = room.floorY + room.height + 0.25;
  const hw = width * 0.5 + 0.45;
  const t = 0.7;
  if (side === 'n' || side === 's') {
    const z = side === 'n' ? room.z1 : room.z0;
    return new THREE.Box3(
      new THREE.Vector3(along - hw, y0, z - t),
      new THREE.Vector3(along + hw, y1, z + t),
    );
  }
  const x = side === 'e' ? room.x1 : room.x0;
  return new THREE.Box3(
    new THREE.Vector3(x - t, y0, along - hw),
    new THREE.Vector3(x + t, y1, along + hw),
  );
}
